(() => {
// Track section instances to prevent duplicate listeners after theme-editor reloads.
  const instances = new WeakMap();

  function initialize(component) {
    if (instances.has(component)) return;
    const events = new AbortController();
    const on = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: events.signal });

// Route floating arrows through native controls so the theme still owns carousel looping.
    on(component, 'click', (event) => {
      const arrow = event.target.closest('.slider-float-arrow');
      if (arrow) {
        const direction = arrow.classList.contains('slider-float-arrow--prev') ? 'previous' : 'next';
        component.querySelector(`button[name="${direction}"]`)?.click();
        return;
      }
      if (event.target.closest('a, button, video[controls]')) return;
      const slide = event.target.closest('.slideshow__slide[data-slide-link]');
      if (slide && !dragged) window.location.href = slide.dataset.slideLink;
    });
// A swipe or page-scroll gesture must not activate the whole-slide link.
    let startX = 0;
    let startY = 0;
    let dragged = false;
    on(component, 'touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      dragged = false;
    }, { passive: true });
    on(component, 'touchmove', (event) => {
      dragged ||= Math.abs(event.touches[0].clientX - startX) > 15 ||
        Math.abs(event.touches[0].clientY - startY) > 15;
    }, { passive: true });

    const videos = [...component.querySelectorAll('.slideshow__video')];
    if (!videos.length) {
      instances.set(component, () => events.abort());
      return;
    }
    const slides = [...component.querySelectorAll('.slideshow__slide')];
    const automatic = component.slider.dataset.autoplay === 'true' && slides.length > 1;
// Save native image timers before adapting hover/focus controls to video playback.
    const originalPlay = component.play.bind(component);
    const originalPause = component.pause.bind(component);
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const failed = new WeakSet();
    let paused = false;
    let visible = false;
    let disposed = false;
    let playbackAttempt = 0;
    let activationTimer;
    let transitioning = false;
    // Match the picture/CSS breakpoint. A mobile image can override a default video.
    const phoneViewport = window.matchMedia('(max-width: 749px)');
    const videoForSlide = (index) => {
      const viewport = phoneViewport.matches ? 'mobile' : 'desktop';
      return slides[index]?.querySelector(
        `.slideshow__video[data-viewport="all"], .slideshow__video[data-viewport="${viewport}"]`
      );
    };
    const activeVideo = () => videoForSlide((component.currentPage || 1) - 1);
    let preloadTimer;
    const allowed = () => !disposed && !paused && !transitioning && visible && !document.hidden && !motion.matches;
// Sources stay inert until playback or guarded next-slide preloading; hydrate once.
    const hydrate = (video) => {
      const template = video.querySelector('.slideshow__video-sources');
      if (!template) return;
      video.append(template.content);
      template.remove();
      video.load();
    };
    // Warm only the immediate next slide and its matching viewport variant. Preloading
    // is a browser hint, not playback; never start hidden videos or bypass data-saving preferences.
    const warmNext = () => {
      const connection = navigator.connection;
      if (!automatic || !allowed() || document.readyState !== 'complete' ||
          connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;
      const current = activeVideo();
      if (current && !failed.has(current)) {
        if (current.paused || current.currentTime < 0.75 || current.readyState < 3) return;
        // Leave bandwidth for the current animation until it has three seconds buffered
        // (or its remaining duration, for clips approaching their end).
        let ahead = 0;
        for (let i = 0; i < current.buffered.length; i++) {
          if (current.buffered.start(i) <= current.currentTime && current.buffered.end(i) >= current.currentTime) {
            ahead = current.buffered.end(i) - current.currentTime;
            break;
          }
        }
        const remaining = Number.isFinite(current.duration) ? current.duration - current.currentTime : 3;
        if (ahead < Math.min(3, remaining)) return;
      }
      const next = videoForSlide((component.currentPage || 1) % slides.length);
      if (!next || failed.has(next) || !next.querySelector('.slideshow__video-sources')) return;
      next.preload = 'auto';
      hydrate(next);
    };
    const stop = () => {
// Invalidate pending play promises when a newer pause/resume attempt takes over.
      clearTimeout(preloadTimer);
      playbackAttempt += 1;
      originalPause();
      videos.forEach((video) => video.pause());
    };
    const resume = () => {
      stop();
      if (!allowed()) return;
      const video = activeVideo();
// Images and failed videos use the configured interval instead of stalling automatic rotation.
      if (!video || failed.has(video)) {
        if (automatic) {
          originalPlay();
          preloadTimer = setTimeout(warmNext, 1500);
        }
        return;
      }
      if (video.ended) {
        if (automatic) component.autoRotateSlides();
        return;
      }
      hydrate(video);
      video.muted = true;
      const attempt = playbackAttempt;
      video.play().catch((error) => {
// Stale or aborted play attempts during navigation are not media failures.
        if (attempt !== playbackAttempt || error.name === 'AbortError' || disposed || activeVideo() !== video || !allowed()) return;
        failed.add(video);
        if (automatic) originalPlay();
      });
    };
    // Existing hover, focus and autoplay controls call these methods dynamically.
    component.play = () => { paused = false; resume(); };
    component.pause = () => { paused = true; stop(); };
    // Scroll snapping can report several intermediate slides during one swipe.
    // Stop the outgoing clip immediately, then start only the settled destination.
    // Do not seek inactive clips: unnecessary seeks can compete with the next stream.
    on(component, 'slideChanged', () => {
      transitioning = true;
      stop();
      clearTimeout(activationTimer);
      videos.forEach((video) => {
        video.closest('.slideshow__media').classList.remove('slideshow__media--playing');
      });
      activationTimer = setTimeout(() => {
        transitioning = false;
        if (disposed) return;
        // Navigation is a request to play the selected slide. Preserve an explicit
        // pause-button choice, but release an incidental focus pause from the old slide.
        paused = component.autoplayButtonIsSetToPlay === false;
        const video = activeVideo();
        if (video && video.readyState > 0 && video.currentTime > 0) video.currentTime = 0;
        resume();
      }, 150);
    });
    videos.forEach((video) => {
      const template = video.querySelector('.slideshow__video-sources');
      const sources = [...(template ? template.content : video).querySelectorAll('source')];
// One unsupported source is recoverable; fall back after all candidate sources fail.
      const failedSources = new Set();
      const failVideo = () => {
        failed.add(video);
        video.closest('.slideshow__media').classList.remove('slideshow__media--playing');
        if (activeVideo() === video) resume();
      };
// Hide the poster only when playback begins, avoiding a blank frame during loading.
      on(video, 'playing', () => {
        if (activeVideo() === video && allowed()) {
          video.closest('.slideshow__media').classList.add('slideshow__media--playing');
        } else {
          // A late playback event from a superseded slide must not revive hidden media.
          video.pause();
        }
      });
// Video duration controls advancement; image slides retain the native timer.
      // Progress events let us wait for actual buffering rather than guessing download speed.
      on(video, 'timeupdate', () => {
        if (activeVideo() === video) warmNext();
      });
      on(video, 'ended', () => {
        if (activeVideo() === video && allowed() && automatic) component.autoRotateSlides();
      });
      on(video, 'error', failVideo);
      sources.forEach((source) => on(source, 'error', () => {
        failedSources.add(source);
        if (failedSources.size === sources.length) failVideo();
      }));
      if (!template && (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE)) failed.add(video);
    });
    // Rotation/resizing can change media type as well as source. Reset both variants,
    // reveal the newly selected poster, and let resume choose video playback or image timing.
    on(phoneViewport, 'change', () => {
      stop();
      videos.forEach((video) => {
        if (video.readyState > 0) video.currentTime = 0;
        video.closest('.slideshow__media').classList.remove('slideshow__media--playing');
      });
      resume();
    });
    // If an image was shown while the page loaded, retry after initial page resources finish.
    on(window, 'load', () => {
      if (!activeVideo() && allowed()) preloadTimer = setTimeout(warmNext, 1500);
    });
    on(document, 'visibilitychange', resume);
    on(motion, 'change', resume);
// Pause offscreen media; two animation frames allow a poster paint before starting playback.
    const observer = new IntersectionObserver(([entry]) => {
      if (visible === entry.isIntersecting) return;
      visible = entry.isIntersecting;
      if (!visible) stop();
      else requestAnimationFrame(() => requestAnimationFrame(resume));
    }, { threshold: 0.01 });
    observer.observe(component);
// Release observers/listeners on section removal and invalidate pending playback work.
    instances.set(component, () => {
      disposed = true;
      clearTimeout(activationTimer);
      stop();
      observer.disconnect();
      events.abort();
    });
    resume();
  }

  const mount = (root) => root.querySelectorAll('slideshow-component').forEach(initialize);
// Wait for the native carousel, then support initial load and theme-editor section replacements.
  customElements.whenDefined('slideshow-component').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
    } else mount(document);
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('slideshow-component').forEach((component) => {
        instances.get(component)?.();
        instances.delete(component);
      });
    });
  });
})();
